import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Globe, Mail, Bot, Users, ArrowRight, 
  CheckCircle, Cloud, Wrench, Monitor 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageSEO } from "@/components/PageSEO";

const services = [
  {
    icon: Globe,
    title: "Website Building",
    description: "Custom websites that drive results. From landing pages to complex web applications.",
    color: "from-cyan-500 to-cyan-600",
    link: "/website-design-uk"
  },
  {
    icon: Bot,
    title: "AI Agents & Automation",
    description: "Intelligent AI agents and workflow automation to streamline your business operations.",
    color: "from-purple-500 to-purple-600",
    link: "/ai-automation-agents-uk"
  },
];

const features = [
  { icon: Cloud, text: "Cloud Solutions" },
  { icon: Wrench, text: "Website Support" },
  { icon: Monitor, text: "24/7 Monitoring" },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="pt-16 lg:pt-20">
      <PageSEO
        title="Website Building, AI Agents & IT Services in Lowestoft, Suffolk"
        description="Your-IT-Solutions — professional website building, AI agents & automation, Microsoft 365, and IT consulting. Based in Lowestoft, Suffolk, serving businesses across the UK. No build fees."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-gradient overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-float" style={{ animationDelay: "2s" }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-400 text-sm font-medium mb-6 border border-cyan-500/20">
                Your Technology Partner
              </span>
              <h1 
                data-testid="hero-title"
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6"
              >
                Transform Your Business with{" "}
                <span className="gradient-text">Smart IT Solutions</span>
              </h1>
              <p className="text-lg text-gray-400 mb-8 max-w-xl leading-relaxed">
                From website development to AI-powered automation, 
                we provide end-to-end technology solutions that drive growth and efficiency.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-10">
                <Link to="/contact">
                  <Button 
                    data-testid="hero-cta-primary"
                    size="lg"
                    className="btn-primary text-white font-medium px-8 h-12"
                  >
                    Get Started <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    data-testid="hero-cta-secondary"
                    variant="outline"
                    size="lg"
                    className="border-gray-700 text-white hover:bg-white/5 font-medium px-8 h-12"
                  >
                    Explore Services
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap gap-6">
                {features.map((feature) => (
                  <div 
                    key={feature.text}
                    className="flex items-center gap-2 text-gray-400"
                  >
                    <feature.icon size={16} className="text-cyan-500" />
                    <span className="text-sm">{feature.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-2xl overflow-hidden border border-gray-800 glow-cyan">
              <img 
                src="/images/server-rack.webp"
                alt="Your-IT-Solutions professional technology services"
                className="w-full h-[500px] object-cover"
                loading="eager"
                fetchPriority="high"
              />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">
              What We Offer
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Comprehensive IT Services
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We support On-premise, Hybrid, and Cloud setups to meet your unique business needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="service-card group p-8 rounded-2xl bg-gray-900/50 border border-gray-800 hover:border-cyan-500/30"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6`}>
                  <service.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <Link 
                  to={service.link}
                  className="inline-flex items-center text-cyan-400 text-sm font-medium hover:text-cyan-300 transition-colors"
                >
                  Learn More <ArrowRight size={16} className="ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 lg:py-32 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Technology That Works for You
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                We combine technical expertise with a deep understanding of business needs 
                to deliver solutions that truly make a difference.
              </p>

              <div className="space-y-4">
                {[
                  "Expert team with 10+ years of experience",
                  "Flexible solutions for On-premise, Hybrid & Cloud",
                  "24/7 dedicated support and monitoring",
                  "Transparent pricing with no hidden costs",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-cyan-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/about" className="inline-block mt-8">
                <Button 
                  data-testid="why-us-cta"
                  className="btn-primary text-white font-medium px-8"
                >
                  Learn About Us <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden border border-gray-800">
                <img 
                  src="https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxuZXR3b3JrJTIwaW5mcmFzdHJ1Y3R1cmUlMjBjYWJsZXMlMjB0ZWNobm9sb2d5JTIwZGFya3xlbnwwfHx8fDE3NzUyOTU0Mzl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Your-IT-Solutions network infrastructure and technology"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-gray-900 border border-gray-800 rounded-xl p-6 hidden lg:block">
                <div className="text-3xl font-bold text-white mb-1">10+</div>
                <div className="text-gray-400 text-sm">Years Experience</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Transform Your IT Infrastructure?
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help your business grow with the right technology solutions.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button 
                  data-testid="cta-contact"
                  size="lg"
                  className="btn-primary text-white font-medium px-8 h-12"
                >
                  Contact Us Today
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  data-testid="cta-pricing"
                  variant="outline"
                  size="lg"
                  className="border-gray-700 text-white hover:bg-white/5 font-medium px-8 h-12"
                >
                  View Pricing
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
